import React, {useState, useContext, createRef} from 'react';
import {useQueryClient} from "react-query";
import {Link} from "react-router-dom";
import __ from "lodash";
import {FormGroup, FormControl, Grid, Button, TextField, InputAdornment, IconButton, useTheme} from "@material-ui/core";
import {OpenInBrowser} from "@material-ui/icons";

import {MaskedText, Form, LabelledPaper, FormCheckbox, MaskedTime} from '../../../controls';
import SystemContext from "../../../SystemContext";
import CountryLookup from "../../Settings/components/CountryLookup";
import CountryNames from "../../Settings/components/CountryNames";
import AirportSlotRequirementList from "./AirportSlotRequirementList";
import AirportValidator from "../validators/AirportsValidator";
import Timings from "./AirportTimingsGrid";
import {
  useUpdateAirport,
  useAirportTimings,
} from "../../../services/airports";
import {useAirportServiceTypes, useComboValues} from "../../../services/settings";
import AirportLookup from "./AirportsLookup";
import AirportServiceProvidersGrid from "./AirportServiceProvidersGrid";
import AirportNotamLineEditor from "./AirportNotamLineEditor";
import AirportFuelRestrictionEditor from './AirportFuelRestrictionEditor';


const AirportDetailsForm = (_props) => {
  const props = {
    canEdit: false,
    ..._props
  }
  const classes = props.classes;
  const SystemData = useContext(SystemContext);
  const theme = useTheme();

  const [record, setRecord] = useState(props.record);
  const [oldRecord, setOldRecord] = useState({});
  const [editing, setEditing] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: ""
  });
  const [serviceProvidersGridApi, setServiceProvidersGridApi] = useState(null);
  const [timingGridApi, setTimingGridApi] = useState(null);

  const eventBus = SystemData.eventBus;
  if (eventBus) {
    eventBus.bind('AirportList.rowSelected', (record)=> {
      setRecord(record);
      setOldRecord(record);
      setEditing(false);
    });
  }

  const extraKeys = [];
  const tableRef = createRef({state: {lastEditingRow: false}});
  const handleUpdateAirport = useUpdateAirport();
  const queryClient = useQueryClient();

  const timingsExtraKeys = [record?record.airportCode:""];
  const airportTimings = useAirportTimings({tableRef, queryClient, extraKeys: timingsExtraKeys});
  const airportServiceProviders = useAirportServiceTypes({queryClient, extraKeys:timingsExtraKeys});
  if (airportServiceProviders.data)
    record["airportServiceProviders"] = __.cloneDeep(airportServiceProviders.data);
  if (airportTimings.data)
    record["airportTimings"] = __.cloneDeep(airportTimings.data);

  const comboExtraKeys = ["CARGO LOAD PROFILE", ""];
  const comboValues = useComboValues({queryClient, extraKeys: comboExtraKeys})

  const formatChars={
    "U": "[A-Z]",
    "A": "[A-Z0-9]",
    "a": "[A-Za-z0-9_\/]",
    "9": "[0-9]",
    "D": "[0-9.]",
    "-": "-",
    "x": "."
  }

  return (
    <div key="formContainer" style={{width: "100%", textAlign:"left"}}>
      {record ?
        <Form
          key="form"
          title={(record)=>{return `Airport Details for ${record.airportName}`}}
          message=""
          open={true}
          record={record}
          validator={editing?AirportValidator:null}
          top={"calc(100vh - 274px)"}
          formTitleStyle={{padding: "16px 16px"}}
          titleBarStyle={{backgroundColor: '#3f3f3f', color: theme.palette.primary.main, padding: "6px"}}
          formBodyStyle={{maxHeight: "calc(100vh - 360px)"}}
          buttonBarStyle={{width: "calc(100% - 16px)"}}
          buttons = {editing?
            {
              addNotam: {icon: "", key: "Add", label: "Add NOTAM", color: "primary", disabled: (status)=>{return status.error}},
              addFuelRestriction: {icon: "", key: "Add Fuel Restriction", label: "Add Fuel Restriction", color: "primary", disabled: (status)=>{return status.error}},
              save:{icon: "check_circle", key: "Save", label: "Save", color: "primary", disabled: (status)=>{
                  if (status.buttonState && status.buttonState.disabled) return true;
                  return status.error}
              },
              cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
            }: props.canEdit?{
              edit: {icon: "edit", key: "Edit", label: "Edit", color: "primary"}
            }: {}
          }

          // validator={AddTripValidator}
          onChange={(state) => {
            if (state.buttonPressed === "edit" && props.canEdit) {
              setEditing(true);
              if (serviceProvidersGridApi) {
                setTimeout(function() { serviceProvidersGridApi.redrawRows(); }, 0);
              }
              if (timingGridApi) {
                setTimeout(function() { timingGridApi.redrawRows(); }, 0);
              }
              eventBus.trigger('AirportNotam.AddLine', 'edit');
              eventBus.trigger('AirportFuelRestriction.AddLine', 'edit');
            }
            if (state.buttonPressed === "cancel") {
              setEditing(false);
              setRecord(oldRecord);
              record["airportServiceProviders"] = airportServiceProviders.data;
              record["airportTimings"] = airportTimings.data;
              if (serviceProvidersGridApi) {
                setTimeout(function() { serviceProvidersGridApi.redrawRows(); }, 0);
              }
              if (timingGridApi) {
                setTimeout(function() { timingGridApi.redrawRows(); }, 0);
              }
              eventBus.trigger('AirportNotam.AddLine', 'cancel');
              eventBus.trigger('AirportFuelRestriction.AddLine', 'cancel');
            }
            if (state.buttonPressed === "save") {
              eventBus.trigger('AirportNotam.AddLine', 'cancel');
              eventBus.trigger('AirportFuelRestriction.AddLine', 'cancel');
              if (serviceProvidersGridApi) {
                setTimeout(function () {
                  serviceProvidersGridApi.redrawRows();
                }, 0);
              }
              if (timingGridApi) {
                setTimeout(function () {
                  timingGridApi.redrawRows();
                }, 0);
              }
              SystemData.toggleLoadingProgress(true);
              const record = state.record;
              handleUpdateAirport.mutate({
                extraKeys,
                record,
                postSuccess: () => {
                  setEditing(false);
                  SystemData.toggleLoadingProgress(false);
                  setAlert({severity: "info", open: true, message: "Record Updated", timeOut: 5});
                  queryClient.invalidateQueries("ServiceTypes");
                  queryClient.invalidateQueries("AirportTimings");
                },
                postError: (data) => {
                  SystemData.toggleLoadingProgress(false);
                  if (data.errorMessage) {
                    SystemData.alert("Error", `${data.errorMessage}`);
                  } else {
                    SystemData.alert("Error", `An Unexpected Error has occurred updating the Airport record.`);
                  }
                }
              });
            } else if (state.buttonPressed === "addNotam") {
              eventBus.trigger('AirportNotam.AddLine');
            } else if (state.buttonPressed === "addFuelRestriction") {
              eventBus.trigger('AirportFuelRestriction.AddLine');
            } else {
            }
          }}
        >

          <Grid container checkChildren={true} spacing={3}>

            <Grid item xs={12} checkChildren={true}>
              <LabelledPaper checkChildren={true} label="Airport Classification">
                <FormControl checkChildren={true} key="FC-classification" style={{display: "block"}}>
                  <FormGroup checkChildren={true} key="FG-classification" row>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={1} checkChildren={true}>
                        <FormControl checkChildren={true} >
                          <FormCheckbox
                            id="isHub"
                            key="isHub"
                            label="Hub Airport"
                            className={classes.checkbox}
                            readOnly={editing!==true}
                            checked={record.isHub}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={1} />
                      <Grid item xs={3} checkChildren={true}>
                        <FormControl checkChildren={true} style={{display: "block"}}>
                          <MaskedText
                            id="airportCode"
                            label="IATA Code"
                            readOnly={editing!==true}
                            inputLabelProps={{shrink: true}}
                            fullWidth
                            mask={"A".repeat(20)}
                            maskChar={""}
                            alwaysShowMask={false}
                            formatChars={formatChars}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={3} checkChildren={true}>
                        <FormControl checkChildren={true} style={{display: "block"}}>
                          <MaskedText
                            id="icaoCode"
                            label="ICAO Code"
                            readOnly={editing!==true}
                            inputLabelProps={{shrink: true}}
                            fullWidth
                            mask={"A".repeat(20)}
                            maskChar={""}
                            alwaysShowMask={false}
                            formatChars={formatChars}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={1} />
                      <Grid item xs={2} checkChildren={true}>
                        {(record) => (
                          <MaskedText
                            id="groundHandlingAppCode"
                            label="Ground Handling App Code"
                            inputLabelProps={{ shrink: true }}
                            readOnly={editing !== true}
                            style={{width: "100%"}}
                            mask={"9999-9999"}
                            maskChar={""}
                            alwaysShowMask={false}
                            formatChars={{ 9: "[0-9]" }}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} />
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            <Grid item xs={12} checkChildren={true}>
              <LabelledPaper checkChildren={true} label="Airport Details">
                <FormControl checkChildren={true} key="FC-airportdetails" style={{display: "block"}} >
                  <FormGroup checkChildren={true} key="FG-airportdetails" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={6} checkChildren={true}>
                        <MaskedText
                          id="airportName"
                          label="Airport Name"
                          readOnly={editing!==true}
                          fullWidth
                          mask={"x".repeat(100)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={formatChars}
                        />
                      </Grid>
                      <Grid item xs={3} checkChildren={true}>
                        <CountryLookup
                          id="countryCode"
                          label="Country Code"
                          readOnly={editing!==true}
                          inputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          value={record.countryCode}
                          returnType={"object"}
                          onChange={(returnObject, props) => {
                            if (returnObject.found) {
                              //setEditRow({...editRow, countryCode: returnObject.data.countryCode, countryName: returnObject.data.countryName});
                            } else {
                              //setEditRow({...editRow, countryCode: returnObject.value, countryName: ""});
                            }
                            props.onChange(returnObject.value);
                          }}
                        />
                        {record => (
                          <CountryNames
                            countryCode={record.countryCode}
                            onChange={(country, props) => {
                              if (!__.isEmpty(country) && country.countryName !== props.record.countryName) {
                                const data = {
                                  ...props.record,
                                  countryName: country.countryName,
                                }
                                props.recordChange(data);
                              }
                            }}
                          />
                        )}
                      </Grid>
                      <Grid item xs={3} checkChildren={true}>
                        <MaskedText
                          id="countryName"
                          label="Country Name"
                          readOnly={true}
                          inputLabelProps={{shrink: true}}
                          fullWidth
                          mask={"x".repeat(100)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={formatChars}
                        />
                      </Grid>

                      <Grid item xs={12} />
                      <Grid item xs={2} checkChildren={true}>
                        <MaskedText
                          id="timeZone"
                          label="Time Zone"
                          readOnly={editing!==true}
                          inputLabelProps={{shrink: true}}
                          fullWidth
                          mask={"a".repeat(50)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={formatChars}
                        />
                      </Grid>
                      <Grid item xs={2} checkChildren={true}>
                        <MaskedText
                          id="longitude"
                          label="Longitude"
                          readOnly={editing!==true}
                          inputLabelProps={{shrink: true}}
                          fullWidth
                          mask={"D".repeat(50)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={formatChars}
                        />
                      </Grid>
                      <Grid item xs={2} checkChildren={true}>
                        <MaskedText
                          id="latitude"
                          label="Latitude"
                          readOnly={editing!==true}
                          inputLabelProps={{shrink: true}}
                          fullWidth
                          mask={"D".repeat(50)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={formatChars}
                        />
                      </Grid>
                      <Grid item xs={2} checkChildren={true}>
                        <MaskedTime
                          id="curfewFrom"
                          label="Curfew From (UTC)"
                          inputLabelProps={{shrink: true}}
                          fullWidth
                          readOnly={editing!==true}
                          allowNull={true}
                        />
                      </Grid>
                      <Grid item xs={2} checkChildren={true}>
                        <MaskedTime
                          id="curfewTo"
                          label="Curfew To (UTC)"
                          inputLabelProps={{shrink: true}}
                          fullWidth
                          readOnly={editing!==true}
                          allowNull={true}
                        />
                      </Grid>
                      <Grid item xs={2} checkChildren={true}>
                        <AirportLookup
                          id="alternateAirportCode"
                          label="Alternate"
                          inputLabelProps={{ shrink: true }}
                          fullWidth
                          readOnly={editing!==true}
                          value={record.alternateAirportCode}
                          returnType={"object"}
                          onChange={(returnObject, props) => {
                            if (returnObject.found) {
                              const airport = returnObject.data
                              const data = {
                                ...props.record,
                                alternateAirportCode: airport.airportCode,
                                alternateAirportIcaoCode: airport.icaoCode,
                              }
                              props.recordChange(data);
                            } else {
                              props.onChange(returnObject.value);
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            <Grid item xs={12} checkChildren={true}>
              <LabelledPaper checkChildren={true} label="Airport NOTAMs">
                <FormControl checkChildren={true} key="FC-airportnotams" style={{display: "block"}} >
                  <FormGroup checkChildren={true} key="FG-airportnotams" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={12} checkChildren={true}>
                        {(record, changeRecord, changeButtonState) => (
                          <AirportNotamLineEditor
                            airportCode={record.airportCode}
                            canEdit={editing}
                            classes={classes}
                            changeButtonState={changeButtonState}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            <Grid item xs={12} checkChildren={true}>
              <LabelledPaper checkChildren={true} label="Airport Fuel Restrictions">
                <FormControl checkChildren={true} key="FC-airportnotams" style={{display: "block"}} >
                  <FormGroup checkChildren={true} key="FG-airportnotams" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>
                      <Grid item xs={12} checkChildren={true}>
                        {(record, changeRecord, changeButtonState) => (
                          <div style={{height: 200}}>
                            <AirportFuelRestrictionEditor
                              airportCode={record.airportCode}
                              canEdit={editing}
                              classes={classes}
                              changeButtonState={changeButtonState}
                              mode="AIRPORT"
                            />
                          </div>
                        )}
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            <Grid item xs={12} checkChildren={true} >
              <LabelledPaper checkChildren={true} label="Airport Slot Information">
                <FormControl checkChildren={true} key="FC-airportslots" style={{display: "block"}} >
                  <FormGroup checkChildren={true} key="FG-airportslots" style={{padding: "8px 8px 8px 8px", flexDirection: "row"}}>
                    <Grid item xs={9} checkChildren={true}>
                      <Grid container checkChildren={true} spacing={3}>
                        <Grid item xs={4} checkChildren={true}>
                          <MaskedText
                            id="slotBookingEmail"
                            label="Slot Booking Email"
                            inputLabelProps={{shrink: true}}
                            readOnly={editing!==true}
                            fullWidth
                            mask={"x".repeat(100)}
                            maskChar={""}
                            alwaysShowMask={false}
                            formatChars={formatChars}
                          />
                        </Grid>
                        <Grid item xs={4} checkChildren={true}>
                          {(record) => (
                            <TextField
                              id="slotBookingOnlinePortal"
                              label="Slot Booking Portal"
                              readOnly={editing!==true}
                              shrink="true"
                              InputLabelProps={{ shrink: true }}
                              style={{width: "100%"}}
                              fullWidth
                              InputProps={{
                                readOnly: editing!==true,
                                shrink: "true",
                                endAdornment:
                                  <InputAdornment position="end">
                                    <Link
                                      to={{pathname: record.slotBookingOnlinePortal && record.slotBookingOnlinePortal.toUpperCase().startsWith("WWW") ? `https://${record.slotBookingOnlinePortal}`: record.slotBookingOnlinePortal}}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <IconButton
                                        aria-label="Open in browser"
                                        edge="end"
                                      >
                                        {record.slotBookingOnlinePortal ? <OpenInBrowser /> : <></>}
                                      </IconButton>
                                    </Link>
                                  </InputAdornment>
                              }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={3} checkChildren={true}>
                          {(record) => (
                            <TextField
                              id="slotBookingWebsite"
                              label="Slot BookingWebsite"
                              readOnly={editing!==true}
                              shrink="true"
                              InputLabelProps={{ shrink: true }}
                              style={{width: "100%"}}
                              fullWidth
                              InputProps={{
                                readOnly: editing!==true,
                                shrink: "true",
                                endAdornment:
                                  <InputAdornment position="end">
                                    <Link
                                      to={{pathname: record.slotBookingWebsite && record.slotBookingWebsite.toUpperCase().startsWith("WWW") ? `https://${record.slotBookingWebsite}`: record.slotBookingWebsite}}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <IconButton
                                        aria-label="Open in browser"
                                        edge="end"
                                      >
                                        {record.slotBookingWebsite ? <OpenInBrowser /> : <></>}
                                      </IconButton>
                                    </Link>
                                  </InputAdornment>
                              }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={11} checkChildren={true}>
                          <MaskedText
                            id="slotBookingNotes"
                            label="Slot Booking Notes"
                            readOnly={editing!==true}
                            inputLabelProps={{shrink: true}}
                            fullWidth
                            mask={"x".repeat(200)}
                            maskChar={""}
                            alwaysShowMask={false}
                            formatChars={formatChars}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={3} checkChildren={true}>
                      <AirportSlotRequirementList id="airportCode" airportCode={timingsExtraKeys}/>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            <Grid item xs={12} checkChildren={true}>
              <LabelledPaper checkChildren={true} label="Default Service Providers">
                <FormControl checkChildren={true} key="FC-services" style={{display: "block"}} >
                  <FormGroup checkChildren={true} key="FG-services" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid item xs={12} checkChildren={true}>
                      {(record) => (
                        <AirportServiceProvidersGrid
                          airportCode={record.airportCode}
                          services={record.airportServiceProviders}
                          canEdit={editing}
                          height={400}
                          onGridReady={ params => {
                            setServiceProvidersGridApi(params.api);
                          }}
                          onChange={(airportServiceProviders, props) =>{
                            const currentAirportServiceProviders = props.record.airportServiceProviders;
                            if (airportServiceProviders && Array.isArray(airportServiceProviders)) {
                              airportServiceProviders.forEach(service => {
                                const currentService = __.find(currentAirportServiceProviders, {servicesTypeUuid: service.servicesTypeUuid});
                                if(currentService) {
                                  currentService.companyUuid = service.companyUuid;
                                  currentService.serviceProviderName = service.serviceProviderName;
                                }
                              });
                              const data = {
                                ...props.record,
                                airportServiceProviders: currentAirportServiceProviders
                              }
                              props.recordChange(data);
                            }
                          }}
                        />
                      )}
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            <Grid item xs={12} checkChildren={true}>
              <LabelledPaper checkChildren={true} label="Airport Turn Timings">
                <FormControl checkChildren={true} key="FC-cargoload" style={{display: "block"}} >
                  <FormGroup checkChildren={true} key="FG-cargoload" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid item xs={12} checkChildren={true}>
                      {(record) => (
                        <Timings
                          timings={record.airportTimings}
                          canEdit={editing}
                          height={300}
                          onGridReady={ params => {
                            setTimingGridApi(params.api);
                          }}
                          onChange={(timings, props) => {
                            if (timings) {
                              const data = {
                                ...props.record,
                                airportTimings: timings
                              }
                              props.recordChange(data);
                            }
                          }}
                        />
                      )}
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

          </Grid>
        </Form>
        :
        <div style={{ position: "relative", width: "100%", top: "50%", height: "calc(100vh - 220px)", textAlign: "center"}}>
          <Button color={"primary"}>Select an Airport to view additional details</Button>
        </div>
      }
    </div>
  );
}

export default AirportDetailsForm;
